import React, { useEffect, useMemo, useState } from "react";
import ComponentCustomModal from "../../../components/common/customModal/ComponentCustomModal";
import Loader from "../../../components/common/Loader";
import { FieldArray, Form, Formik } from "formik";
import useDataInputOption from "../../../hooks/useDataInputOption";
import { FtextField } from "../../../components/common/FtextField";
import { useDispatch, useSelector } from "react-redux";
import {
  assignNetworkDecice,
  reassignNetworkDiagramDevice,
} from "../../../features/oltApi";
import * as Yup from "yup";
import { getCustomer } from "../../../features/apiCalls";
import useISPowner from "../../../hooks/useISPOwner";
import { useTranslation } from "react-i18next";

const AssignDeviceDialog = ({
  show,
  setShow,
  isUpdate,
  parent,
  children = [],
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //   const children = true;

  // get user & current user data form useISPOwner hooks
  const { ispOwnerId } = useISPowner();

  //---> Get netwok device from redux store
  const devices = useSelector((state) => state.network?.devices);
  console.log({ devices });

  //---> Get network device outputs form redux store
  const outputs = useSelector((state) => state.network?.outputs);

  //---> Get customers form redux store
  const customers = useSelector((state) => state.customer.customer);

  //---> Local State
  const [isLoading, setIsLoading] = useState(false);
  const [deviceOutputs, setDeviceOutput] = useState([]);
  const [filterDevice, setFilterDevice] = useState([]);
  const [deviceType, setDeviceType] = useState("");

  const handleDeviceOutput = (id) => {
    console.log({ outputs, id });

    const output = outputs.filter((item) => item.netWorkDevice === id);
    setDeviceOutput(output);
  };

  const handleGetDevicesByDeviceType = (value) => {
    const filteredDevices = devices.filter(
      (item) => item.candidateType === value,
    );

    setFilterDevice(filteredDevices);
    setDeviceType(value);
  };

  // =================> device options
  const deviceOptions = useMemo(() => {
    return (
      devices?.map((item) => ({
        name: item?.name,
        value: item?.id,
      })) || []
    );
  }, [devices]);

  const inputOption = [
    {
      as: "select",
      id: "deviceType",
      name: "deviceType",
      label: t("deviceType"),
      options: [
        { name: t("mikrotik"), value: "mikrotik" },
        { name: t("olt"), value: "olt" },
        { name: t("switch"), value: "switch" },
        { name: t("splitter"), value: "splitter" },
        { name: t("onu"), value: "onu" },
      ],
      firstOptions: t("selectDeviceType"),
      textAccessor: "name",
      valueAccessor: "value",
      isVisible: true,
      validation: true,
      onChange: (event) => handleGetDevicesByDeviceType(event.target.value),
    },
    {
      as: "select",
      id: "device",
      name: "device",
      label: t("device"),
      options: filterDevice,
      firstOptions: t("deviceType"),
      textAccessor: "name",
      valueAccessor: "id",
      isVisible: true,
      validation: true,
      onChange: (event) => handleDeviceOutput(event.target.value),
    },
    {
      as: "select",
      id: "customer",
      name: "customer",
      label: t("customer"),
      options: customers?.map((c) => ({
        name: c?.name,
        value: c?.id,
      })),
      firstOptions: t("customer"),
      textAccessor: "name",
      valueAccessor: "value",
      isVisible: deviceType === "onu",
      validation: true,
      showOn: "onu", //  key part
    },
    {
      type: "text",
      id: "location",
      name: "location",
      label: t("location"),
      isVisible: true,
      validation: true,
    },
  ];

  const initialValues = {
    deviceType: "",
    device: "",
    customer: "",
    location: "",
    outputs:
      children?.map((child) => ({
        parent: "",
        output: child?.data?._id || "",
      })) || [],
  };

  const validationSchema = Yup.object({
    deviceType: Yup.string().required(t("deviceTypeRequired")),

    device: Yup.string().required(t("deviceRequired")),

    customer: Yup.string().when("deviceType", {
      is: "onu",
      then: (schema) => schema.required(t("customerRequired")),
      otherwise: (schema) => schema.nullable(),
    }),

    location: Yup.string()
      .trim()
      .required(t("locationRequired"))
      .min(2, t("locationMinLength")),

    outputs: Yup.array().of(
      Yup.object().shape({
        parent: Yup.string().required(t("portRequired")),
        output: Yup.string().required(t("outputRequired")),
      }),
    ),
  });

  const findDeviceById = (id) => {
    const device = devices.find((item) => item.id === id);

    return device;
  };

  const findDeviceOutputById = (id) => {
    const output = outputs.find((item) => item.id === id);

    return output;
  };

  const handleSubmit = (values) => {
    console.log(values);
    handleNetworkAssignFormSubmission(values);
  };

  const handleNetworkAssignFormSubmission = (formValues) => {
    if (isUpdate) {
      const sendingData = {
        ...formValues,
        parent: parent.data._id,
      };

      reassignNetworkDiagramDevice(
        dispatch,
        sendingData,
        setIsLoading,
        setShow,
      );
    } else {
      const sendingData = {
        ...formValues,
        parent: parent._id,
      };

      assignNetworkDecice(dispatch, sendingData, setIsLoading, setShow);
    }
  };

  useEffect(() => {
    //---> @Get ispOwner all customer data
    !customers?.length && getCustomer(dispatch, ispOwnerId, setIsLoading);
  }, []);

  return (
    <>
      <ComponentCustomModal
        {...{
          show,
          setShow,
          size: "lg",
          header: isUpdate ? t("assingNewDevice") : t("reassingNewDevice"),
          footer: (
            <div className="displayGrid1 float-end">
              <button
                type="button"
                className="btn btn-secondary border-0"
                disabled={isLoading}
                onClick={() => setShow(false)}
              >
                {t("cancel")}
              </button>

              <button
                type="submit"
                form="assignDeviceForm"
                className="btn btn-success border-0"
                disabled={isLoading}
              >
                {isLoading ? <Loader /> : t("save")}
              </button>
            </div>
          ),
        }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {() => (
            <Form id="assignDeviceForm">
              <div className="displayGrid">
                <div className="displayGrid2">
                  {inputOption?.map(
                    (item) =>
                      item?.isVisible && (
                        <FtextField key={item.name} {...item} />
                      ),
                  )}
                </div>

                {children?.length > 0 && (
                  <FieldArray name="outputs">
                    {({ form }) =>
                      children?.map((child, index) => {
                        console.log({ child });

                        const parentDevice = parent
                          ? findDeviceById(parent.data.device)
                          : null;

                        const output = child
                          ? findDeviceOutputById(child.data.output)
                          : null;

                        const childrenDevice = child
                          ? findDeviceById(child.data.device)
                          : null;

                        const displayText =
                          parentDevice && output && childrenDevice
                            ? `${parentDevice.name} output ${output.portName}-(${childrenDevice.name})`.toUpperCase()
                            : "N/A";

                        return (
                          <div
                            key={index}
                            className="gridTemplateManualColumn4_6"
                          >
                            <div>
                              <FtextField
                                name={`outputs.${index}.parent`}
                                as="select"
                                id={`outputs-${index}-parent`}
                                options={deviceOutputs?.map(
                                  ({ portName, id }) => ({
                                    text: portName,
                                    value: id,
                                  }),
                                )}
                                firstOptions={t("bandwidth")}
                                textAccessor="text"
                                valueAccessor="value"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  form.setFieldValue(
                                    `outputs.${index}.parent`,
                                    value,
                                  );

                                  form.setFieldValue(
                                    `outputs.${index}.output`,
                                    child?.data._id,
                                  );
                                }}
                              />
                            </div>

                            <div>
                              <FtextField
                                name={`outputs.${index}.output`}
                                as="select"
                                id={`outputs-${index}-output`}
                                options={[
                                  {
                                    text: displayText,
                                    value: child?.data._id || "",
                                  },
                                ]}
                                textAccessor="text"
                                valueAccessor="value"
                                disabled={true}
                                style={{ cursor: "not-allowed" }}
                              />
                            </div>
                          </div>
                        );
                      })
                    }
                  </FieldArray>
                )}
              </div>
            </Form>
          )}

          {/* {({ values }) => (
            <Form id="assignDeviceForm">
              <div className="displayGrid2">
                {inputOption.map((item) => {
                  //  condition check
                  if (item.showOn && item.showOn !== values.deviceType) {
                    return null;
                  }

                  return (
                    item.isVisible && <FtextField key={item.name} {...item} />
                  );
                })}

                {children?.length > 0 && (
                  <FieldArray name="outputs">
                    {({ form }) =>
                      children?.map((child, index) => {
                        console.log({ child });

                        const parentDevice = parent
                          ? findDeviceById(parent.data.device)
                          : null;

                        const output = child
                          ? findDeviceOutputById(child.data.output)
                          : null;

                        const childrenDevice = child
                          ? findDeviceById(child.data.device)
                          : null;

                        const displayText =
                          parentDevice && output && childrenDevice
                            ? `${parentDevice.name} output ${output.portName}-(${childrenDevice.name})`.toUpperCase()
                            : "N/A";

                        return (
                          <div
                            key={index}
                            className="gridTemplateManualColumn4_6"
                          >
                            <div>
                              <FtextField
                                name={`outputs.${index}.parent`}
                                as="select"
                                id={`outputs-${index}-parent`}
                                options={deviceOutputs?.map(
                                  ({ portName, id }) => ({
                                    text: portName,
                                    value: id,
                                  }),
                                )}
                                firstOptions={t("bandwidth")}
                                textAccessor="text"
                                valueAccessor="value"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  form.setFieldValue(
                                    `outputs.${index}.parent`,
                                    value,
                                  );

                                  form.setFieldValue(
                                    `outputs.${index}.output`,
                                    child?.data._id,
                                  );
                                }}
                              />
                            </div>

                            <div>
                              <FtextField
                                name={`outputs.${index}.output`}
                                as="select"
                                id={`outputs-${index}-output`}
                                options={[
                                  {
                                    text: displayText,
                                    value: child?.data._id || "",
                                  },
                                ]}
                                textAccessor="text"
                                valueAccessor="value"
                                disabled={true}
                                style={{ cursor: "not-allowed" }}
                              />
                            </div>
                          </div>
                        );
                      })
                    }
                  </FieldArray>
                )}
              </div>
            </Form>
          )} */}
        </Formik>
      </ComponentCustomModal>
    </>
  );
};

export default AssignDeviceDialog;
